/**
 * Gunfire, voiced (67.18): the action clock's shots, impacts and near misses, synthesized in Web Audio like the
 * rest of the SFX (no files). `gunfireMix.ts` decides how each is heard — loudness, the air's filtering, the
 * speed-of-sound delay, the side, the tail; this file only makes the sound. Cheap by construction: one shared
 * noise buffer per context (each burst reads it from a random offset — no per-shot allocation), a few nodes a
 * sound, and a voice cap that drops the newest sound past it. Presentation only.
 */
import { getAudioContext, getSfxBus, initAudio, duckMusic } from './sfx';
import { musicDuckFor, ABOVE, STONE_VOICE, ICE_VOICE, type StoneVoice, type BlastKind, type Heard, type ImpactMaterial, type NearMiss, type ReloadPart, type ShotKind, type StepKind, type RocketPass, type DigPart } from './gunfireMix';
import type { GroundMaterial } from '../types/ground';
import type { ShellAftermath } from './shellVoice';

const MAX_VOICES = 40;
let active = 0;
let noise: { ctx: BaseAudioContext; buf: AudioBuffer } | null = null;

function context(): AudioContext | null {
  initAudio();
  const ctx = getAudioContext();
  return ctx && ctx.state !== 'closed' ? ctx : null;
}

export function noiseBuf(ctx: BaseAudioContext): AudioBuffer {
  if (noise?.ctx === ctx) return noise.buf;
  const len = ctx.sampleRate * 2, buf = ctx.createBuffer(1, len, ctx.sampleRate), d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  noise = { ctx, buf };
  return buf;
}

/** One voice (shared with gunshipSound.ts: its recipes are built from these parts): everything in it runs through gain → the air's low-pass → the side → the SFX bus, starting `delay` from now. */
export interface Voice { ctx: BaseAudioContext; t: number; input: GainNode }
/** The gain → air → side chain into `dest`, its sound starting at `t`. */
export function chain(ctx: BaseAudioContext, dest: AudioNode, h: Pick<Heard, 'gain' | 'cutoff' | 'pan'>, t: number): { v: Voice; side: StereoPannerNode } {
  const input = ctx.createGain(); input.gain.value = h.gain;
  const air = ctx.createBiquadFilter(); air.type = 'lowpass'; air.frequency.value = h.cutoff; air.Q.value = 0.5;
  const side = ctx.createStereoPanner(); side.pan.value = h.pan;
  input.connect(air); air.connect(side); side.connect(dest);
  return { v: { ctx, t, input }, side };
}
export function voice(h: Pick<Heard, 'gain' | 'cutoff' | 'delay' | 'pan'>, length: number): Voice | null {
  if (active >= MAX_VOICES || h.gain < 0.004) return null;
  const ctx = context(), bus = getSfxBus();
  if (!ctx || !bus) return null;
  const t = ctx.currentTime + 0.01 + h.delay;
  const { v, side } = chain(ctx, bus, h, t);
  active++;
  const end = (t - ctx.currentTime + length + 0.1) * 1000;
  setTimeout(() => { active--; try { side.disconnect(); } catch { /* gone */ } }, end);
  return v;
}

/** A noise burst into the voice: filtered, an attack and an exponential decay. */
export function burst(v: Voice, o: { at?: number; dur: number; vol: number; type: BiquadFilterType; freq: number; Q?: number; freqTo?: number; attack?: number }): void {
  const t = v.t + (o.at ?? 0), src = v.ctx.createBufferSource();
  src.buffer = noiseBuf(v.ctx);
  const f = v.ctx.createBiquadFilter(); f.type = o.type; f.frequency.setValueAtTime(o.freq, t); f.Q.value = o.Q ?? 0.7;
  if (o.freqTo) f.frequency.exponentialRampToValueAtTime(o.freqTo, t + o.dur);
  const g = v.ctx.createGain();
  g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(o.vol, t + (o.attack ?? 0.002)); g.gain.exponentialRampToValueAtTime(0.0008, t + o.dur);
  src.connect(f); f.connect(g); g.connect(v.input);
  const len = o.dur + 0.05;
  src.start(t, Math.random() * Math.max(0, src.buffer.duration - len), len);   // a random stretch of the shared noise, never past its end
}

/** A tone into the voice, sweeping `freq` → `to`. */
export function tone(v: Voice, o: { at?: number; dur: number; vol: number; freq: number; to?: number; type?: OscillatorType }): void {
  const t = v.t + (o.at ?? 0), osc = v.ctx.createOscillator();
  osc.type = o.type ?? 'sine'; osc.frequency.setValueAtTime(o.freq, t);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t + o.dur);
  const g = v.ctx.createGain();
  g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(o.vol, t + 0.003); g.gain.exponentialRampToValueAtTime(0.0008, t + o.dur);
  osc.connect(g); g.connect(v.input);
  osc.start(t); osc.stop(t + o.dur + 0.05);
}

/**
 * 65.26, the .50 cal's voice (the owner: "50 cal doesn't sound good, should sound better" — it was the gunner's rifle):
 * a heavy gun's report — a deep, hard report, a mechanical clank as the bolt cycles, a longer tail, heard further (it
 * reads from the diorama). Three on the sound sheet, beside today's; `HMG_VOICE` is the one the game plays.
 * - `crack`: the LMG's recipe made heavier — a lower, longer crack over a deep thump.
 * - `thump`: a deep boom and a hard crack, the clank of the bolt just after.
 * - `pound`: the thump with a second pressure wave under it, slower to die, the clank further back.
 */
export type HmgVoice = 'crack' | 'thump' | 'pound';
export const HMG_VOICES: readonly HmgVoice[] = ['crack', 'thump', 'pound'];
export const HMG_VOICE: HmgVoice = 'crack';
/** The bolt cycling: a short metallic clank (a ringing noise band and a falling square), `at` seconds after the report. */
function clank(v: Voice, at: number, vol: number): void {
  burst(v, { at, dur: 0.035, vol: 0.4 * vol, type: 'bandpass', freq: 3600, Q: 6 });
  tone(v, { at, dur: 0.03, vol: 0.12 * vol, freq: 1150, to: 720, type: 'square' });
  burst(v, { at: at + 0.012, dur: 0.05, vol: 0.18 * vol, type: 'bandpass', freq: 2300, Q: 4 });
}
function hmgInto(v: Voice, which: HmgVoice): void {
  if (which === 'crack') {
    burst(v, { dur: 0.075, vol: 1, type: 'bandpass', freq: 1500, Q: 0.7 });
    tone(v, { dur: 0.24, vol: 0.85, freq: 95, to: 32 });
  } else if (which === 'thump') {
    burst(v, { dur: 0.06, vol: 0.95, type: 'bandpass', freq: 1250, Q: 0.6 });
    burst(v, { dur: 0.14, vol: 0.7, type: 'lowpass', freq: 420 });
    tone(v, { dur: 0.32, vol: 1, freq: 72, to: 28 });
    clank(v, 0.045, 1);
  } else {
    burst(v, { dur: 0.065, vol: 0.9, type: 'bandpass', freq: 1100, Q: 0.6 });
    burst(v, { dur: 0.18, vol: 0.75, type: 'lowpass', freq: 360 });
    tone(v, { dur: 0.34, vol: 1, freq: 68, to: 26 });
    tone(v, { at: 0.03, dur: 0.45, vol: 0.6, freq: 46, to: 24 });
    clank(v, 0.07, 0.9);
  }
}
/** The recipe for a shot of `kind` into `v` (a heavy gun by `hmg`, the game's HMG_VOICE unless the sheet asks for another). */
function shotInto(v: Voice, kind: ShotKind, h: Heard, hmg: HmgVoice = HMG_VOICE): void {
  if (kind === 'gl40') { gl40Into(v, GL40_VOICE, h); return; }   // 67.55: its own report (the owner: "thump is better than bloop")
  if (kind === 'hmg') {
    hmgInto(v, hmg);
    burst(v, { at: 0.01, dur: h.tail, vol: 0.3, type: 'lowpass', freq: 700 });   // the longer, darker tail
    return;
  }
  if (kind === 'rocket' || kind === 'missile') {   // (65.49 step 2: the anti-dropship missile's launch as the LAW's, unplayed)
    // the LAW: the launch's pop (a sharp, wide crack), a low thump under it, then the motor's short burn — a rising roar
    // that thins as the rocket leaves (its back-blast is a voice of its own, behind the tube: playBackblast)
    burst(v, { dur: 0.03, vol: 0.9, type: 'bandpass', freq: 2600, Q: 0.5 });
    tone(v, { dur: 0.22, vol: 0.7, freq: 90, to: 38 });
    burst(v, { at: 0.02, dur: 0.12, vol: 0.55, type: 'lowpass', freq: 600 });
    burst(v, { at: 0.03, dur: 0.55, vol: 0.4, type: 'bandpass', freq: 700, freqTo: 2400, Q: 0.9 });
  } else if (kind === 'panzerfaust') {
    // 65.36: the recoilless launch — no motor: a wide pop, a deep whoomp, the gas thrown through the open tube (its back-blast its own voice)
    burst(v, { dur: 0.04, vol: 0.95, type: 'bandpass', freq: 1600, Q: 0.4 });
    tone(v, { dur: 0.3, vol: 0.9, freq: 70, to: 30 });
    burst(v, { at: 0.005, dur: 0.35, vol: 0.6, type: 'lowpass', freq: 900, freqTo: 250 });
    burst(v, { at: 0.01, dur: 0.18, vol: 0.35, type: 'bandpass', freq: 500, Q: 0.6 });
  } else if (kind === 'garand') {
    // 65.36: the .30-06 semi — the rifle's crack lower and fuller, a push of air under it
    burst(v, { dur: 0.06, vol: 1, type: 'bandpass', freq: 1900, Q: 0.7 });
    tone(v, { dur: 0.15, vol: 0.7, freq: 125, to: 40 });
    burst(v, { dur: 0.08, vol: 0.35, type: 'lowpass', freq: 500 });
  } else if (kind === 'springfield') {
    // 65.36: the bolt gun's crack — a hard, sharp front over the .30-06's body, ringing out longer (its tail, SHOT_HEARD)
    burst(v, { dur: 0.012, vol: 0.5, type: 'highpass', freq: 4500 });
    burst(v, { dur: 0.055, vol: 1, type: 'bandpass', freq: 2300, Q: 0.6 });
    tone(v, { dur: 0.16, vol: 0.65, freq: 135, to: 42 });
  } else if (kind === 'bar') {
    // 65.36: the BAR's slower thump — the .30-06 from a heavy gun, more low body than the LMG
    burst(v, { dur: 0.065, vol: 0.95, type: 'bandpass', freq: 1700, Q: 0.7 });
    tone(v, { dur: 0.2, vol: 0.8, freq: 100, to: 34 });
    burst(v, { dur: 0.1, vol: 0.4, type: 'lowpass', freq: 380 });
  } else if (kind === 'thompson') {
    // 65.36: the Thompson's lower chatter — the .45 from a heavy open bolt: a lower crack than the SMG's, a thicker body, the bolt's clack
    burst(v, { dur: 0.035, vol: 0.85, type: 'bandpass', freq: 2100, Q: 0.9 });
    tone(v, { dur: 0.075, vol: 0.4, freq: 140, to: 55 });
    burst(v, { at: 0.02, dur: 0.02, vol: 0.12, type: 'bandpass', freq: 3000, Q: 5 });
  } else if (kind === 'm1911') {
    // 65.36: the 1911's heavier pop — the .45 lower and rounder than the pistol's crack
    burst(v, { dur: 0.045, vol: 0.9, type: 'bandpass', freq: 2300, Q: 0.9 });
    tone(v, { dur: 0.1, vol: 0.5, freq: 170, to: 60 });
    burst(v, { dur: 0.05, vol: 0.3, type: 'lowpass', freq: 420 });
  } else if (kind === 'pistol') {
    burst(v, { dur: 0.035, vol: 0.85, type: 'bandpass', freq: 3200, Q: 0.8 });
    tone(v, { dur: 0.07, vol: 0.3, freq: 220, to: 80 });
  } else if (kind === 'smg') {
    // 65.13: lighter and snappier than the rifle, a brighter crack and a thin body (it fires twice as often)
    burst(v, { dur: 0.03, vol: 0.85, type: 'bandpass', freq: 2900, Q: 0.8 });
    tone(v, { dur: 0.06, vol: 0.3, freq: 180, to: 70 });
  } else if (kind === 'lmg') {
    // 65.14: the rifle's crack with more weight under it
    burst(v, { dur: 0.055, vol: 0.95, type: 'bandpass', freq: 2100, Q: 0.7 });
    tone(v, { dur: 0.14, vol: 0.65, freq: 120, to: 40 });
  } else {
    burst(v, { dur: 0.05, vol: 0.95, type: 'bandpass', freq: 2400, Q: 0.7 });
    tone(v, { dur: 0.12, vol: 0.55, freq: 140, to: 45 });
  }
  burst(v, { at: 0.01, dur: h.tail * (kind === 'pistol' ? 0.6 : kind === 'm1911' ? 0.7 : 1), vol: 0.22, type: 'lowpass', freq: 900 });
}

/** A shot: the crack, the body's thump, the reverberant tail (longer far off and among trees). */
export function playShot(kind: ShotKind, h: Heard, hmg?: HmgVoice): void {
  const v = voice(h, Math.max(0.3, h.tail));
  if (!v) return;
  duckMusic(musicDuckFor(h.gain));   // 67.22: the music dips under a loud shot
  shotInto(v, kind, h, hmg);
}
/** The sound sheet's look at a shot: rendered into `ctx` (an offline context) from `at` seconds, as the game would play it. */
export function renderShot(ctx: BaseAudioContext, kind: ShotKind, h: Heard, at = 0.01, hmg?: HmgVoice): void {
  shotInto(chain(ctx, ctx.destination, h, at).v, kind, h, hmg);
}

/**
 * 65.36, the classic guns' actions (gunfireMix BOLT_BEATS, CLIP_PING), heard as a reload's parts are (`hearReload`):
 * - the Garand's clip thrown clear on its last round — the ping (a bright, ringing strip of spring steel) and its landing;
 * - the Springfield's bolt, in two parts as the drawn one moves: `open` (the handle lifted — the cocking cam's click — and
 *   drawn back, the case thrown out with a tinkle) and `shut` (pushed forward, stripping a round in, and turned down: the lock).
 */
export type BoltPart = 'open' | 'shut';
export const BOLT_PARTS: readonly BoltPart[] = ['open', 'shut'];
function clipPingInto(v: Voice): void {
  burst(v, { dur: 0.01, vol: 0.4, type: 'highpass', freq: 5000 });              // the clip's kick off the follower
  tone(v, { dur: 0.45, vol: 0.35, freq: 2650 });                                 // the ping
  tone(v, { dur: 0.3, vol: 0.18, freq: 4100 });
  tone(v, { dur: 0.15, vol: 0.08, freq: 6900 });
  tone(v, { at: 0.55, dur: 0.08, vol: 0.1, freq: 3100, to: 2900 });             // it lands, and bounces once
  burst(v, { at: 0.55, dur: 0.04, vol: 0.12, type: 'lowpass', freq: 600 });
  tone(v, { at: 0.68, dur: 0.05, vol: 0.05, freq: 3000, to: 2850 });
}
function boltInto(v: Voice, part: BoltPart): void {
  if (part === 'open') {
    burst(v, { at: 0.1, dur: 0.008, vol: 0.55, type: 'highpass', freq: 3800 });                          // the handle up, cocked
    tone(v, { at: 0.1, dur: 0.02, vol: 0.08, freq: 1600, to: 1200, type: 'square' });
    burst(v, { at: 0.16, dur: 0.17, vol: 0.3, type: 'bandpass', freq: 1400, freqTo: 2400, Q: 2 });       // drawn back
    burst(v, { at: 0.33, dur: 0.012, vol: 0.35, type: 'highpass', freq: 3000 });                         // at its stop, the case thrown
    tone(v, { at: 0.36, dur: 0.06, vol: 0.08, freq: 3800, to: 3600 });
    tone(v, { at: 0.44, dur: 0.05, vol: 0.06, freq: 3300, to: 3150 });
  } else {
    burst(v, { dur: 0.18, vol: 0.3, type: 'bandpass', freq: 2200, freqTo: 1300, Q: 2 });                 // forward, a round stripped in
    burst(v, { at: 0.19, dur: 0.02, vol: 0.3, type: 'bandpass', freq: 1100, Q: 2 });                     // home
    burst(v, { at: 0.3, dur: 0.007, vol: 0.7, type: 'highpass', freq: 3000 });                           // turned down: locked
    burst(v, { at: 0.3, dur: 0.04, vol: 0.35, type: 'bandpass', freq: 900, Q: 2 });
    tone(v, { at: 0.3, dur: 0.04, vol: 0.08, freq: 1900, type: 'square' });
  }
}
/** The Garand's clip ping heard (`hearReload`'s numbers). */
export function playClipPing(h: Heard): void {
  const v = voice(h, 0.8);
  if (v) clipPingInto(v);
}
/** The sound sheet's look at the clip ping, rendered into `ctx` from `at` seconds. */
export function renderClipPing(ctx: BaseAudioContext, h: Heard, at = 0.01): void {
  clipPingInto(chain(ctx, ctx.destination, h, at).v);
}
/** A part of the Springfield's bolt cycle heard (`hearReload`'s numbers). */
export function playBolt(part: BoltPart, h: Heard): void {
  const v = voice(h, 0.5);
  if (v) boltInto(v, part);
}
/** The sound sheet's look at a part of the bolt cycle, rendered into `ctx` from `at` seconds. */
export function renderBolt(ctx: BaseAudioContext, part: BoltPart, h: Heard, at = 0.01): void {
  boltInto(chain(ctx, ctx.destination, h, at).v, part);
}

/**
 * 65.31's own sound (67.55; the 40 mm's report is the LAW's today, `shotKindOf`): the break-barrel launcher's hollow
 * report and its action. Two voices on the sound sheet for the owner, `GL40_VOICE` the one the game would play:
 * - `thump`: a deep, hollow "thoonk" — a short pop, a resonant body falling from 180 Hz, a soft low push.
 * - `bloop`: the M79's famous "bloop" — a pitched, ringing tone falling from 320 Hz through a narrow band, a lighter pop.
 * The break action, in four parts (the reload's beats): `open` (the latch thumbed aside, the barrel dropping on its
 * hinge), `caseOut` (the spent brass tipped out, a tinkle and a soft landing), `roundIn` (a fresh round slid into the
 * chamber), `close` (the barrel snapped shut, the latch catching).
 */
export type Gl40Voice = 'thump' | 'bloop';
export const GL40_VOICES: readonly Gl40Voice[] = ['thump', 'bloop'];
export const GL40_VOICE: Gl40Voice = 'thump';
export type Gl40Part = 'open' | 'caseOut' | 'roundIn' | 'close';
export const GL40_PARTS: readonly Gl40Part[] = ['open', 'caseOut', 'roundIn', 'close'];
function gl40Into(v: Voice, which: Gl40Voice, h: Heard): void {
  if (which === 'thump') {
    burst(v, { dur: 0.025, vol: 0.55, type: 'bandpass', freq: 1800, Q: 0.6 });
    tone(v, { dur: 0.16, vol: 0.85, freq: 180, to: 70, type: 'triangle' });
    burst(v, { at: 0.005, dur: 0.12, vol: 0.5, type: 'bandpass', freq: 260, Q: 3 });
    tone(v, { dur: 0.22, vol: 0.5, freq: 70, to: 35 });
  } else {
    burst(v, { dur: 0.02, vol: 0.4, type: 'bandpass', freq: 2200, Q: 0.7 });
    tone(v, { dur: 0.2, vol: 0.8, freq: 320, to: 120, type: 'sine' });
    burst(v, { at: 0.004, dur: 0.18, vol: 0.55, type: 'bandpass', freq: 240, freqTo: 140, Q: 9 });
    tone(v, { dur: 0.14, vol: 0.35, freq: 90, to: 50 });
  }
  burst(v, { at: 0.01, dur: Math.max(0.15, h.tail * 0.7), vol: 0.16, type: 'lowpass', freq: 700 });
}
function gl40PartInto(v: Voice, part: Gl40Part): void {
  if (part === 'open') {
    burst(v, { dur: 0.012, vol: 0.5, type: 'highpass', freq: 3500 });                               // the latch
    burst(v, { at: 0.07, dur: 0.06, vol: 0.45, type: 'bandpass', freq: 1300, Q: 3 });              // the barrel dropping onto its stop
    tone(v, { at: 0.07, dur: 0.05, vol: 0.12, freq: 900, to: 650, type: 'square' });
  } else if (part === 'caseOut') {
    burst(v, { dur: 0.05, vol: 0.25, type: 'bandpass', freq: 1800, freqTo: 1200, Q: 2 });          // drawn out of the chamber
    tone(v, { at: 0.18, dur: 0.07, vol: 0.18, freq: 4200, to: 3900 });                             // the brass ringing as it lands
    tone(v, { at: 0.26, dur: 0.05, vol: 0.1, freq: 3600, to: 3400 });
    burst(v, { at: 0.18, dur: 0.05, vol: 0.2, type: 'lowpass', freq: 500 });                       // on the ground
  } else if (part === 'roundIn') {
    burst(v, { dur: 0.09, vol: 0.25, type: 'bandpass', freq: 1200, freqTo: 2200, Q: 1.5 });        // slid along the bore
    burst(v, { at: 0.09, dur: 0.03, vol: 0.4, type: 'bandpass', freq: 900, Q: 2 });                // seated
  } else {
    burst(v, { dur: 0.015, vol: 0.75, type: 'highpass', freq: 2500 });                             // the snap shut
    burst(v, { dur: 0.06, vol: 0.5, type: 'bandpass', freq: 700, Q: 1.5 });
    tone(v, { at: 0.01, dur: 0.05, vol: 0.18, freq: 1400, to: 900, type: 'square' });             // the latch catching
  }
}
/** The 40 mm's report heard (`hearShot`'s numbers for its kind). */
export function playGl40(h: Heard, which: Gl40Voice = GL40_VOICE): void {
  const v = voice(h, Math.max(0.3, h.tail));
  if (!v) return;
  duckMusic(musicDuckFor(h.gain * 0.7));
  gl40Into(v, which, h);
}
/** The sound sheet's look at the 40 mm's report, rendered into `ctx` from `at` seconds. */
export function renderGl40(ctx: BaseAudioContext, h: Heard, at = 0.01, which: Gl40Voice = GL40_VOICE): void {
  gl40Into(chain(ctx, ctx.destination, h, at).v, which, h);
}
/** A part of the 40 mm's break action heard (`hearReload`'s numbers). */
export function playGl40Part(part: Gl40Part, h: Heard): void {
  const v = voice(h, 0.4);
  if (v) gl40PartInto(v, part);
}
/** The sound sheet's look at a part of the break action, rendered into `ctx` from `at` seconds. */
export function renderGl40Part(ctx: BaseAudioContext, part: Gl40Part, h: Heard, at = 0.01): void {
  gl40PartInto(chain(ctx, ctx.destination, h, at).v, part);
}

/**
 * 67.25a: a blast, voiced through the heard chain (its loudness, the air's cut, the delay, the side): the crack, the low
 * body falling away, the sub thump, and the tail; a shell's (and a heavy one's) a second pulse of body behind the first.
 */
function blastInto(v: Voice, kind: BlastKind, h: Heard): void {
  const big = kind !== 'small';
  burst(v, { dur: big ? 0.07 : 0.05, vol: 0.7, type: 'bandpass', freq: big ? 4000 : 3200, Q: 0.4 });
  burst(v, { dur: big ? 1.0 : 0.6, vol: 0.9, type: 'lowpass', freq: big ? 260 : 220, freqTo: big ? 40 : 60 });
  if (big) burst(v, { at: 0.12, dur: 0.7, vol: 0.55, type: 'lowpass', freq: 300, freqTo: 50 });
  tone(v, { dur: big ? 0.6 : 0.3, vol: big ? 0.7 : 0.5, freq: big ? 50 : 65, to: big ? 18 : 25 });
  burst(v, { at: 0.02, dur: big ? 0.45 : 0.35, vol: 0.35, type: 'bandpass', freq: 1400, freqTo: 400, Q: 0.5 });
  burst(v, { at: 0.05, dur: h.tail, vol: 0.3, type: 'lowpass', freq: 500 });
}
/** A blast heard (hearBlast's numbers). */
export function playBlast(kind: BlastKind, h: Heard): void {
  const v = voice(h, Math.max(1.2, h.tail + 0.1));
  if (!v) return;
  duckMusic(musicDuckFor(h.gain));
  blastInto(v, kind, h);
}
/** The sound sheet's look at a blast, rendered into `ctx` from `at` seconds. */
export function renderBlast(ctx: BaseAudioContext, kind: BlastKind, h: Heard, at = 0.01): void {
  blastInto(chain(ctx, ctx.destination, h, at).v, kind, h);
}

/**
 * 67.71: a strike round's burst, after the burst (shellVoice.ts `shellAftermath`; through the burst's own heard chain, so
 * its delay, side and the air's cut): the debris coming down near it — stones ticking and clods thudding, thick at first
 * and lighter as it thins out — and the rumble rolling away, by distance (far, the boom's roll is most of what arrives).
 */
function aftermathInto(v: Voice, a: ShellAftermath): void {
  for (let i = 0; a.debris > 0 && i < a.bits; i++) {
    const u = Math.random(), at = 0.35 + 1.65 * u * u, light = 1 - 0.55 * (at - 0.35) / 1.65, vol = a.debris * light * (0.45 + 0.55 * Math.random());
    if (Math.random() < 0.6) burst(v, { at, dur: 0.015 + 0.02 * Math.random(), vol, type: 'bandpass', freq: 1800 + 2700 * Math.random(), Q: 2 });   // a stone
    else burst(v, { at, dur: 0.04 + 0.04 * Math.random(), vol: vol * 1.2, type: 'lowpass', freq: 450 + 450 * Math.random() });                     // a clod
  }
  if (a.rumble > 0) {
    const s = a.seconds;
    burst(v, { at: 0.1, dur: s, vol: 0.5 * a.rumble, type: 'lowpass', freq: 190, freqTo: 55, attack: 0.25 });
    burst(v, { at: 0.55 + 0.2 * Math.random(), dur: s * 0.7, vol: 0.32 * a.rumble, type: 'lowpass', freq: 150, freqTo: 45, attack: 0.3 });   // rolling back off the ground round it
    burst(v, { at: 1.2 + 0.3 * Math.random(), dur: s * 0.5, vol: 0.2 * a.rumble, type: 'lowpass', freq: 120, freqTo: 40, attack: 0.35 });
  }
}
/** A strike round's aftermath heard (`shellAftermath`'s numbers, on the burst's `h`). */
export function playShellAftermath(a: ShellAftermath, h: Heard): void {
  if (a.debris <= 0 && a.rumble <= 0) return;
  const v = voice(h, 2.2 + a.seconds);
  if (v) aftermathInto(v, a);
}
/** The sound sheet's look at the aftermath, rendered into `ctx` from `at` seconds. */
export function renderShellAftermath(ctx: BaseAudioContext, a: ShellAftermath, h: Heard, at = 0.01): void {
  aftermathInto(chain(ctx, ctx.destination, h, at).v, a);
}

/** An impact, by what it hit; `speedFrac` (0–1) the round's speed at impact over its muzzle speed (the voice's gain already carries it; here it brightens the hit and decides a ricochet). */
export function playImpact(material: ImpactMaterial, h: Heard, speedFrac: number, heavy = false): void {
  const v = voice(h, heavy ? 0.7 : 0.5);
  if (!v) return;
  duckMusic(musicDuckFor(h.gain * 0.7));
  const s = Math.max(0, Math.min(1, speedFrac)), bright = 0.6 + 0.4 * s;
  // 65.26: a .50's round lands with a deep thump under the material's own sound, and throws more dirt
  if (heavy) {
    tone(v, { dur: 0.16, vol: 0.55, freq: 70, to: 30 });
    if (material === 'ground') burst(v, { at: 0.02, dur: 0.3, vol: 0.3, type: 'lowpass', freq: 900, freqTo: 250 });
  }
  switch (material) {
    case 'ground':
      tone(v, { dur: 0.08, vol: 0.5, freq: 90, to: 40 });
      burst(v, { dur: 0.12 + h.tail, vol: 0.35, type: 'lowpass', freq: 1800 * bright });
      break;
    case 'rock':
      burst(v, { dur: 0.03, vol: 0.6, type: 'highpass', freq: 3000 * bright });
      tone(v, { dur: 0.15, vol: 0.12, freq: 2400 * bright });
      if (Math.random() < 0.35 * s) tone(v, { at: 0.02, dur: 0.35, vol: 0.1, freq: 3000, to: 1200, type: 'triangle' });   // a ricochet's whine
      break;
    case 'wood':
      burst(v, { dur: 0.06, vol: 0.5, type: 'bandpass', freq: 700 * bright, Q: 3 });
      tone(v, { dur: 0.08, vol: 0.25, freq: 260, to: 180 });
      break;
    case 'flesh':
      burst(v, { dur: 0.07, vol: 0.6, type: 'lowpass', freq: 500 * bright });
      tone(v, { dur: 0.06, vol: 0.3, freq: 120, to: 70 });
      break;
    case 'metal':
      burst(v, { dur: 0.02, vol: 0.3, type: 'highpass', freq: 4000 });
      tone(v, { dur: 0.45, vol: 0.16, freq: 1850 * bright });
      tone(v, { dur: 0.4, vol: 0.12, freq: 2900 * bright });
      break;
  }
}

/** A near miss: the supersonic crack, then the whizz sweeping down as it passes (Doppler). */
export function playNearMiss(n: NearMiss): void {
  const v = voice({ gain: n.gain, cutoff: 16000, delay: 0, pan: n.pan }, n.duration + 0.05);
  if (!v) return;
  duckMusic(musicDuckFor(n.gain * 0.8));
  if (n.crack > 0) burst(v, { dur: 0.012, vol: n.crack, type: 'highpass', freq: 2500 });
  burst(v, { at: 0.004, dur: n.duration, vol: 0.7, type: 'bandpass', freq: n.pitchFrom, freqTo: n.pitchTo, Q: 4 });
  if (n.crack === 0) tone(v, { at: 0.004, dur: n.duration, vol: 0.18, freq: n.pitchFrom * 0.7, to: n.pitchTo * 0.7, type: 'triangle' });   // a slow round whirs
  if (n.heavy) burst(v, { at: 0.006, dur: n.duration * 1.2, vol: 0.45, type: 'lowpass', freq: 380, freqTo: 140 });   // 65.26: a .50's push of air
}

/**
 * 67.28: a part of the reload — `out`: the catch's click and the magazine sliding free; `in`: the fresh one sliding in
 * with a soft knock; `seat`: the metallic clack into the receiver (two quick strikes and a short ring). Generic across
 * the weapons for now; each a few small noises.
 */
export function playReload(part: ReloadPart, h: Heard): void {
  const v = voice(h, 0.3);
  if (!v) return;
  if (part === 'out') {
    burst(v, { dur: 0.008, vol: 0.6, type: 'highpass', freq: 4000 });
    burst(v, { at: 0.02, dur: 0.09, vol: 0.35, type: 'bandpass', freq: 1500, freqTo: 900, Q: 2 });
  } else if (part === 'in') {
    burst(v, { dur: 0.08, vol: 0.35, type: 'bandpass', freq: 900, freqTo: 1600, Q: 2 });
    burst(v, { at: 0.07, dur: 0.03, vol: 0.5, type: 'lowpass', freq: 600 });
  } else {
    burst(v, { dur: 0.006, vol: 0.7, type: 'highpass', freq: 3000 });
    burst(v, { at: 0.035, dur: 0.006, vol: 0.8, type: 'highpass', freq: 3200 });
    tone(v, { at: 0.035, dur: 0.06, vol: 0.12, freq: 2200 });
    tone(v, { at: 0.035, dur: 0.05, vol: 0.08, freq: 3400 });
    burst(v, { at: 0.035, dur: 0.03, vol: 0.4, type: 'lowpass', freq: 700 });
  }
}

/**
 * 67.25: one step on the ground under the foot, synthesized: soil a soft thud and a scuff; rock a dull knock with a
 * sharper heel; sand a soft hiss; snow a crunch (a cluster of tiny clicks over a muffled press); ice a hard click with a
 * faint ring; mud a squelch (a falling, wet low-pass); ash a puff; salt a bright crunch. Wet (rain) adds a small splash
 * on hard ground. Prone is a rustle, whatever the ground. Each step a little different (pitch and level).
 */
export function playStep(k: StepKind, h: Heard, stone: StoneVoice = STONE_VOICE, ice: StoneVoice = ICE_VOICE): void {
  const v = voice(h, 0.25);
  if (v) stepInto(v, k, stone, ice);
}
/** 67.80: the sound sheet's look at a step, rendered into `ctx` from `at` seconds (stone and ice by their voices). */
export function renderStep(ctx: BaseAudioContext, k: StepKind, h: Heard, at = 0.01, stone: StoneVoice = STONE_VOICE, ice: StoneVoice = ICE_VOICE): void {
  stepInto(chain(ctx, ctx.destination, h, at).v, k, stone, ice);
}
function stepInto(v: Voice, k: StepKind, stone: StoneVoice, ice: StoneVoice): void {
  const j = 0.85 + Math.random() * 0.3, heavy = 0.7 + 0.3 * k.run;
  if (k.stance === 'prone') { burst(v, { dur: 0.16, vol: 0.25, type: 'bandpass', freq: 1100 * j, Q: 0.8 }); return; }
  const thud = (vol: number, freq = 260) => burst(v, { dur: 0.06, vol: vol * heavy, type: 'lowpass', freq: freq * j, Q: 0.7 });
  switch (k.material) {
    case 'rock':
      if (stone === 'before') { thud(0.5, 320); burst(v, { dur: 0.018, vol: 0.35 * heavy, type: 'bandpass', freq: 1900 * j, Q: 1.4 }); break; }
      // 67.80: a boot on stone — the heel's broadband click (no pitch), a short low knock, a gritty scuff after; running, the toe's tap
      burst(v, { dur: 0.01, vol: 0.14 * heavy, type: 'highpass', freq: 2300 * j });
      thud(0.42, 420);
      burst(v, { at: 0.018 + 0.02 * Math.random(), dur: 0.05 + 0.03 * Math.random(), vol: 0.05 * heavy, type: 'bandpass', freq: 3600 * j, Q: 0.5 });
      if (k.run > 0.5) burst(v, { at: 0.045, dur: 0.008, vol: 0.08 * heavy, type: 'highpass', freq: 2600 * j });
      break;
    case 'ice':
      if (ice === 'before') { burst(v, { dur: 0.012, vol: 0.45 * heavy, type: 'highpass', freq: 2600 * j }); tone(v, { dur: 0.05, vol: 0.05, freq: 2900 * j }); thud(0.3, 300); break; }
      // 67.80, ice: a boot's crisp crack (a broad mid band, no tone), a few ice grains crackling after it, a low thud; running, the sole's scrape
      burst(v, { dur: 0.014, vol: 0.13 * heavy, type: 'bandpass', freq: 1700 * j, Q: 0.5 });
      for (let i = 0; i < 3; i++) burst(v, { at: 0.006 + i * 0.009 + Math.random() * 0.006, dur: 0.006, vol: (0.04 + Math.random() * 0.04) * heavy, type: 'highpass', freq: 3000 });
      thud(0.32, 280);
      if (k.run > 0.5) burst(v, { at: 0.03, dur: 0.08, vol: 0.04 * heavy, type: 'bandpass', freq: 2200 * j, Q: 0.4 });
      break;
    case 'sand':
      burst(v, { dur: 0.09, vol: 0.22 * heavy, type: 'bandpass', freq: 2600 * j, Q: 0.6 }); thud(0.3, 200); break;
    case 'snow':
    case 'salt': {
      const bright = k.material === 'salt' ? 1.4 : 1;
      thud(0.35, 220);
      for (let i = 0; i < 5; i++) burst(v, { at: 0.01 + i * 0.016 + Math.random() * 0.008, dur: 0.01, vol: (0.18 + Math.random() * 0.12) * heavy, type: 'highpass', freq: 2800 * bright * j });
      break;
    }
    case 'mud':
      burst(v, { dur: 0.13, vol: 0.4 * heavy, type: 'lowpass', freq: 900 * j, freqTo: 180, Q: 2.2 }); thud(0.35, 180); break;
    case 'ash':
      burst(v, { dur: 0.08, vol: 0.25 * heavy, type: 'lowpass', freq: 650 * j, Q: 0.5 }); break;
    default:   // soil
      thud(0.5); burst(v, { at: 0.02, dur: 0.05, vol: 0.12 * heavy, type: 'bandpass', freq: 1400 * j, Q: 0.7 });
  }
  if (k.wet && k.material !== 'mud' && k.material !== 'snow') burst(v, { at: 0.005, dur: 0.05, vol: 0.12, type: 'highpass', freq: 3200 * j });
}

/**
 * 68.14: the far fight from above — one continuous bed (looped noise under a low-pass at ABOVE.rumbleCutoff) whose
 * loudness glides to `set(level)` (gunfireMix rumbleLevel); silent until it is set above zero. One per host.
 */
export class FarRumble {
  private gain: GainNode | null = null;
  private level = 0;
  set(level: number): void {
    const l = Math.max(0, Math.min(1, level));
    if (!this.gain) {
      if (l < 0.002) return;
      const ctx = context(), bus = getSfxBus();
      if (!ctx || !bus) return;
      const src = ctx.createBufferSource();
      src.buffer = noiseBuf(ctx); src.loop = true;
      const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = ABOVE.rumbleCutoff; f.Q.value = 0.5;
      const g = ctx.createGain(); g.gain.value = 0;
      src.connect(f); f.connect(g); g.connect(bus); src.start();
      this.gain = g;
    }
    if (Math.abs(l - this.level) < 0.002) return;
    this.level = l;
    const ctx = this.gain.context;
    this.gain.gain.setTargetAtTime(l, ctx.currentTime, 0.25);
  }
}

/**
 * The LAW's back-blast (S7): the gas thrown out of the tube's rear — a wide, dark roar with a hard front, heard from its
 * own place behind the shooter (gunfireMix LAW_HEARD.backblastPeople).
 */
export function playBackblast(h: Heard): void {
  const v = voice(h, 0.6);
  if (!v) return;
  duckMusic(musicDuckFor(h.gain * 0.8));
  backblastInto(v);
}
function backblastInto(v: Voice): void {
  burst(v, { dur: 0.02, vol: 0.7, type: 'highpass', freq: 1800 });
  burst(v, { at: 0.005, dur: 0.35, vol: 0.75, type: 'lowpass', freq: 1400, freqTo: 300 });
  burst(v, { at: 0.02, dur: 0.5, vol: 0.3, type: 'bandpass', freq: 400, Q: 0.6 });
}
/** The sound sheet's look at the back-blast, rendered into `ctx` from `at` seconds. */
export function renderBackblast(ctx: BaseAudioContext, h: Heard, at = 0.01): void {
  backblastInto(chain(ctx, ctx.destination, h, at).v);
}

/** A rocket passing near: the motor's hiss swept down as it goes by (a Doppler fall), a low rumble under it. */
export function playRocketPass(p: RocketPass): void {
  const v = voice({ gain: p.gain, cutoff: 16000, delay: p.delay ?? 0, pan: p.pan }, p.duration + 0.05);
  if (!v) return;
  duckMusic(musicDuckFor(p.gain * 0.6));
  burst(v, { dur: p.duration, vol: 0.7, type: 'bandpass', freq: p.pitchFrom, freqTo: p.pitchTo, Q: 2 });
  burst(v, { dur: p.duration, vol: 0.3, type: 'lowpass', freq: 260 });
}

/** The spent tube landing on the ground: a hollow knock and a short ring on hard ground, a dull thud on soft. */
export function playTubeDrop(material: GroundMaterial, h: Heard): void {
  const v = voice(h, 0.4);
  if (!v) return;
  const hard = material === 'rock' || material === 'ice' || material === 'salt';
  const soft = material === 'sand' || material === 'snow' || material === 'mud' || material === 'ash';
  burst(v, { dur: 0.05, vol: 0.5, type: 'bandpass', freq: 900, Q: 2.5 });
  tone(v, { dur: 0.08, vol: 0.25, freq: 220, to: 160 });
  if (!soft) burst(v, { at: 0.11, dur: 0.035, vol: hard ? 0.35 : 0.2, type: 'bandpass', freq: 1100, Q: 3 });   // it bounces once
  if (hard) tone(v, { at: 0.005, dur: 0.25, vol: 0.06, freq: 1450, type: 'triangle' });   // the tube rings
}

/**
 * 66.8's dig: a stroke's `bite` into the ground or its `spoil` landing, by the ground's material — soil's crunching bite
 * and thud, sand's hiss and soft fall, snow's crunch, mud's suck and splat, ash's dry hush. Rock and ice are refused by
 * the dig, so they say nothing.
 */
export function playDig(material: GroundMaterial, part: DigPart, h: Heard): void {
  if (material === 'rock' || material === 'ice') return;
  const v = voice(h, 0.4);
  if (!v) return;
  const j = 0.9 + Math.random() * 0.2;
  if (part === 'bite') {
    switch (material) {
      case 'sand': burst(v, { dur: 0.22, vol: 0.45, type: 'bandpass', freq: 3200 * j, freqTo: 1800, Q: 0.7 }); burst(v, { dur: 0.05, vol: 0.2, type: 'lowpass', freq: 400 }); break;
      case 'snow': case 'salt': for (let i = 0; i < 6; i++) burst(v, { at: i * 0.018 + Math.random() * 0.01, dur: 0.012, vol: 0.3 + Math.random() * 0.15, type: 'highpass', freq: 2600 * j }); burst(v, { dur: 0.08, vol: 0.2, type: 'lowpass', freq: 500 }); break;
      case 'mud': burst(v, { dur: 0.2, vol: 0.5, type: 'lowpass', freq: 1100 * j, freqTo: 160, Q: 3 }); tone(v, { at: 0.05, dur: 0.1, vol: 0.12, freq: 180, to: 90 }); break;
      case 'ash': burst(v, { dur: 0.16, vol: 0.3, type: 'bandpass', freq: 1500 * j, Q: 0.5 }); break;
      default:   // soil: the blade's crunch into it, a low thud, a little grit
        burst(v, { dur: 0.06, vol: 0.5, type: 'bandpass', freq: 1300 * j, Q: 0.9 });
        burst(v, { dur: 0.08, vol: 0.45, type: 'lowpass', freq: 320 * j });
        burst(v, { at: 0.04, dur: 0.1, vol: 0.15, type: 'highpass', freq: 3500 });
    }
  } else {
    switch (material) {
      case 'sand': case 'ash': burst(v, { dur: 0.25, vol: 0.3, type: 'bandpass', freq: 2400 * j, freqTo: 900, Q: 0.6 }); burst(v, { at: 0.02, dur: 0.06, vol: 0.15, type: 'lowpass', freq: 300 }); break;
      case 'snow': case 'salt': burst(v, { dur: 0.1, vol: 0.3, type: 'lowpass', freq: 900 * j }); for (let i = 0; i < 3; i++) burst(v, { at: 0.02 + i * 0.02, dur: 0.01, vol: 0.15, type: 'highpass', freq: 2400 }); break;
      case 'mud': burst(v, { dur: 0.12, vol: 0.45, type: 'lowpass', freq: 600 * j, Q: 2 }); tone(v, { dur: 0.08, vol: 0.15, freq: 140, to: 70 }); break;
      default:   // soil: the clod's thump and a scatter of crumbs
        burst(v, { dur: 0.09, vol: 0.55, type: 'lowpass', freq: 260 * j });
        burst(v, { at: 0.03, dur: 0.12, vol: 0.12, type: 'bandpass', freq: 2200, Q: 0.6 });
    }
  }
}
